import { ComponentChildren, Ref } from 'preact';
import { forwardRef } from 'preact/compat';
import { useFormContext } from '../hooks/UNSAFE_useFormContext';
import { LabelValueLayout } from '../UNSAFE_LabelValueLayout';
import { StyledComponentProps } from '../utils/UNSAFE_typeUtils';
import { classNames } from '../utils/UNSAFE_classNames';
import { useFormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { Size } from '../utils/UNSAFE_size';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { ReadonlyTextFieldVariantOptions } from './themes/ReadonlyTextFieldStyles.css';
import { TextFieldStyles } from './themes/TextFieldStyles.css';
import { useInputGroupContext } from '../hooks/UNSAFE_useInputGroupContext';
import { useUser } from '../hooks/UNSAFE_useUser';
import { TextFieldRedwoodTheme } from './themes/redwood/TextFieldTheme';
import { LabelRedwoodTheme } from '../UNSAFE_Label/themes/redwood/LabelTheme';
import { FormLayoutRedwoodTheme } from './themes/redwood/FormLayoutTheme';
import { ReadonlyTextFieldRedwoodTheme } from './themes/redwood/ReadonlyTextFieldTheme';
import { type TestIdProps, useTestId } from '#hooks/UNSAFE_useTestId';
import { layoutSpanStyles, LayoutColumnSpan } from '../utils/UNSAFE_styles/Layout';

type DivProps = HTMLAttributesSignalExcluded<HTMLDivElement>;
type UAProps = {
  compactUserAssistance?: ComponentChildren;
  inlineUserAssistance?: ComponentChildren;
};
type InnerOuterClassNamesProps = {
  outerClassNames?: string;
  innerClassNames?: string;
};

type StartTopLabelReadonlyTextFieldProps = Omit<DivProps, 'label'> &
  UAProps &
  InnerOuterClassNamesProps & {
    label?: ComponentChildren;
    labelEdge?: 'start' | 'top';
    labelStartWidth?: Size;
  };

const StartTopLabelReadonlyTextField = forwardRef(
  (
    {
      outerClassNames,
      innerClassNames,
      label,
      labelEdge,
      labelStartWidth,
      compactUserAssistance,
      inlineUserAssistance,
      children,
      ...props
    }: StartTopLabelReadonlyTextFieldProps,
    ref?: Ref<HTMLDivElement>
  ) => {
    return (
      <div {...props} ref={ref} class={outerClassNames}>
        {compactUserAssistance}
        <LabelValueLayout label={label} labelEdge={labelEdge} labelStartWidth={labelStartWidth}>
          <div class={innerClassNames}>{children}</div>
        </LabelValueLayout>
        {inlineUserAssistance}
      </div>
    );
  }
);

type InsideLabelReadonlyTextFieldProps = DivProps & UAProps & InnerOuterClassNamesProps;
const InsideLabelReadonlyTextField = forwardRef(
  (
    {
      outerClassNames,
      innerClassNames,
      compactUserAssistance,
      inlineUserAssistance,
      children,
      ...props
    }: InsideLabelReadonlyTextFieldProps,
    ref?: Ref<HTMLDivElement>
  ) => {
    return (
      <div {...props} ref={ref} class={outerClassNames}>
        {compactUserAssistance}
        <div class={innerClassNames}>{children}</div>
        {inlineUserAssistance}
      </div>
    );
  }
);

export type StyleProps = UAProps & {
  columnSpan?: LayoutColumnSpan;
  label?: ComponentChildren;
  labelEdge?: 'inside' | 'start' | 'top';
  variant?: 'textarea';
};

type Props = TestIdProps & StyledComponentProps<'div', StyleProps>;

export const ReadonlyTextField = forwardRef(
  (
    { columnSpan = 1, label, labelEdge, children, variant, testId, ...props }: Props,
    ref?: Ref<HTMLElement>
  ) => {
    const testIdProps = useTestId(testId);
    const { isFormLayout, isReadonly: isReadonlyForm } = useFormContext();
    const { isLoading } = useFormFieldContext();
    const hasInsideLabel = label !== undefined && labelEdge === 'inside';
    const isInsideNonReadonlyForm = isFormLayout && !isReadonlyForm;
    const { direction } = useUser();
    const isLtr = direction === 'ltr';
    const inputGroupContext = useInputGroupContext();
    const hasGroupContext = inputGroupContext !== null;
    // depending on browser direction, apply the appropriate styling.
    // when 'rtl' the flex row direction is reversed.
    const inputGroupPosition = inputGroupContext
      ? inputGroupContext.last
        ? isLtr
          ? 'groupRight'
          : 'groupLeft'
        : inputGroupContext.index === 0
        ? isLtr
          ? 'groupLeft'
          : 'groupRight'
        : 'groupMiddle'
      : undefined;

    const { classes, styles } = useComponentTheme(TextFieldRedwoodTheme, {
      readonly: 'isReadonly'
    });
    const { baseTheme: labelBaseTheme } = useComponentTheme(LabelRedwoodTheme);
    const { baseTheme: formLayoutTheme } = useComponentTheme(FormLayoutRedwoodTheme);
    const { classes: readonlyClassNames } = useComponentTheme<
      ReadonlyTextFieldVariantOptions,
      TextFieldStyles
    >(ReadonlyTextFieldRedwoodTheme, {
      insideNonReadonlyForm: isInsideNonReadonlyForm
        ? 'isInsideNonReadonlyForm'
        : 'notInsideNonReadonlyForm',
      insideLabel: hasInsideLabel ? 'hasInsideLabel' : 'noInsideLabel',
      textarea: variant === 'textarea' ? 'isTextArea' : 'notTextArea',
      loading: isLoading ? 'isLoading' : 'notLoading',
      withinGroup: hasGroupContext ? 'isWithinGroup' : undefined,
      inputGroupPosition
    });

    const { formControlBase } = styles;
    const outerClassNames = classNames([
      classes,
      formControlBase,
      formLayoutTheme,
      labelBaseTheme,
      layoutSpanStyles.layoutSpanColumn[columnSpan]
    ]);

    if (label !== undefined && (labelEdge === 'start' || labelEdge === 'top')) {
      return (
        <StartTopLabelReadonlyTextField
          outerClassNames={outerClassNames}
          innerClassNames={readonlyClassNames}
          label={label}
          labelEdge={labelEdge}
          ref={ref as Ref<HTMLDivElement> | undefined}
          {...props}
          {...testIdProps}>
          {children}
        </StartTopLabelReadonlyTextField>
      );
    }

    return (
      <InsideLabelReadonlyTextField
        outerClassNames={outerClassNames}
        innerClassNames={readonlyClassNames}
        ref={ref as Ref<HTMLDivElement> | undefined}
        {...props}
        {...testIdProps}>
        {label}
        {children}
      </InsideLabelReadonlyTextField>
    );
  }
);
